import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function AITag({ className }) {
  return (
    <span data-testid="ai-draft-tag" className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200/80", className)}>
      <Sparkles className="w-3 h-3" /> AI draft — review & approve
    </span>
  );
}

export function ApprovedTag({ label = "Adviser approved", className }) {
  return (
    <span data-testid="approved-tag" className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200/80", className)}>
      <ShieldCheck className="w-3 h-3" /> {label}
    </span>
  );
}

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div className="animate-fade-up">
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description && <p className="text-sm text-slate-600 mt-1.5 max-w-2xl leading-relaxed">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
      <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">{label}…</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-2xl border border-dashed border-slate-300 bg-white/50">
      {Icon && <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-emerald-600" /></div>}
      <h3 className="font-display font-semibold text-slate-800">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </motion.div>
  );
}

export function Money({ value, currency = "USD" }) {
  const v = Number(value || 0);
  return <span>{new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(v)}</span>;
}

export const STAGE_COLORS = {
  Lead: "bg-slate-100 text-slate-700 border-slate-200",
  Qualified: "bg-sky-50 text-sky-700 border-sky-200",
  Discovery: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Diagnostic: "bg-violet-50 text-violet-700 border-violet-200",
  Proposal: "bg-amber-50 text-amber-700 border-amber-200",
  Negotiation: "bg-orange-50 text-orange-700 border-orange-200",
  Won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Lost: "bg-rose-50 text-rose-700 border-rose-200",
};

export const HEALTH_COLORS = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
};
